import { permissionEngine } from '@/core/permissions/permissionEngine';
import { useAuth } from '@/core/auth/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Leaf, PackageMinus, Calendar, Info, MapPin, Box, ChevronRight, AlertTriangle, Save } from 'lucide-react';
import { useInventoryStockOut } from './hooks/useInventoryStockOut';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useState } from 'react';
import { cn } from '@/utils/cn';

export function InventoryStockOutPage() {
  const { profile } = useAuth();
  permissionEngine.assert(profile?.id || '', 'inventory.stock_out');

 const { id = '' } = useParams();
 const navigate = useNavigate();
 const {
 item,
 values,
 setValues,
 isLoading,
 isSaving,
 submitStockOut,
 remainingStock,
 } = useInventoryStockOut(id);
 const [confirmOpen, setConfirmOpen] = useState(false);

 const handleSave = async () => {
 const success = await submitStockOut();
 if (success) {
 setConfirmOpen(false);
 navigate('/inventory/items');
 }
 };

 if (isLoading) {
 return (
 <div className="flex flex-col gap-4 pb-8 animate-pulse">
 <div className="h-20 rounded-md bg-white p-3" />
 <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_360px]">
 <div className="h-96 rounded-md bg-white" />
 <div className="h-96 rounded-md bg-white" />
 </div>
 </div>
 );
 }

 if (!item) {
 return (
 <div className="flex flex-col items-center justify-center p-20 text-slate-500 bg-white rounded-md border border-slate-200">
 <Info className="h-12 w-12 mb-4 opacity-20" />
 <p className="">Không tìm thấy thông tin vật tư.</p>
 <button onClick={() => navigate('/inventory/items')} className="mt-4 text-rose-600 hover:underline">Quay lại danh sách</button>
 </div>
 );
 }

 const isOverStock = values.quantity > item.current_stock;

 return (
 <div className="flex flex-col gap-4 pb-8">
 {/* Premium Header */}
 <div className="flex items-center justify-between rounded-md border border-slate-200 bg-white p-3">
 <div className="flex items-center gap-4">
 <button
 type="button"
 onClick={() => navigate('/inventory/items')}
 className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all"
 >
 <ArrowLeft className="h-4 w-4" />
 </button>
 <div>
 <div className="flex items-center gap-2 text-xs text-slate-400 tracking-wider">
 <span>Kho vật tư</span>
 <ChevronRight className="h-3 w-3" />
 <span className="text-rose-600">Xuất kho</span>
 </div>
 <h1 className="sm:text-sm text-xs text-slate-900 tracking-tight">Phiếu xuất kho vật tư</h1>
 </div>
 </div>
 <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-md bg-rose-50 text-rose-600 border border-rose-100">
 <PackageMinus className="h-4 w-4" />
 <span className="sm:text-sm text-xs">Giao dịch xuất</span>
 </div>
 </div>

 <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_360px]">
 {/* Left Side: Professional Form */}
 <div className="space-y-4">
 <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
 <div className="border-b border-slate-100 bg-slate-50/50 px-4 py-3">
 <h2 className="flex items-center gap-2 text-slate-900">
 <Info className="h-4 w-4 text-rose-600" />
 Thông tin xuất kho
 </h2>
 </div>
 
 <div className="p-4 space-y-6">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 <div className="space-y-2">
 <label className="sm:text-sm text-xs text-slate-700 flex items-center gap-1.5">
 Số lượng xuất <span className="text-rose-500">*</span>
 </label>
 <div className="relative">
 <input
 type="number"
 min={0}
 max={item.current_stock}
 value={values.quantity || ''}
 onChange={(e) => setValues(prev => ({ ...prev, quantity: Number(e.target.value) }))}
 className={cn(
"w-full rounded-md border p-3 sm:text-sm text-xs focus:ring-2 focus:outline-none transition-all pr-12",
 isOverStock 
 ?"border-rose-500 text-rose-600 focus:ring-rose-500/10" 
 :"border-slate-200 text-slate-900 focus:border-rose-500 focus:ring-rose-500/10"
 )}
 placeholder="0"
 />
 <div className="absolute right-3 top-1/2 -translate-y-1/2 sm:text-sm text-xs text-slate-400">
 {item.unit}
 </div>
 </div>
 <p className="text-slate-500 italic">Khả dụng: {item.current_stock} {item.unit}</p>
 </div>

 <div className="space-y-2">
 <label className="sm:text-sm text-xs text-slate-700 flex items-center gap-1.5">
 Ngày xuất kho <span className="text-rose-500">*</span>
 </label>
 <div className="relative">
 <input
 type="date"
 value={values.transaction_date}
 onChange={(e) => setValues(prev => ({ ...prev, transaction_date: e.target.value }))}
 className="w-full rounded-md border border-slate-200 p-3 sm:text-sm text-xs text-slate-900 focus:border-rose-500 focus:outline-none transition-all pl-10"
 />
 <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
 </div>
 </div>
 </div>

 {/* Assignment Info */}
 <div className="space-y-4 pt-4 border-t border-slate-50">
 <h3 className="text-xs text-slate-400 tracking-wider">Thông tin bàn giao</h3>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div className="space-y-1.5">
 <label className="text-xs text-slate-700">Dự án sử dụng</label>
 <select 
 className="w-full rounded-md border border-slate-200 p-2.5 sm:text-sm text-xs focus:border-rose-500 focus:outline-none"
 onChange={(e) => setValues(prev => ({ ...prev, project_id: e.target.value }))}
 >
 <option value="">Chọn dự án (nếu có)</option>
 {/* More options would be loaded here */}
 </select>
 </div>
 <div className="space-y-1.5">
 <label className="text-xs text-slate-700">Tổ/Nhóm phụ trách</label>
 <select 
 className="w-full rounded-md border border-slate-200 p-2.5 sm:text-sm text-xs focus:border-rose-500 focus:outline-none"
 onChange={(e) => setValues(prev => ({ ...prev, team_id: e.target.value }))}
 >
 <option value="">Chọn tổ/nhóm nhận</option>
 </select>
 </div>
 </div>
 </div>

 <div className="space-y-2 pt-2 border-t border-slate-50">
 <label className="sm:text-sm text-xs text-slate-700">Ghi chú xuất kho</label>
 <textarea
 value={values.note}
 onChange={(e) => setValues(prev => ({ ...prev, note: e.target.value }))}
 rows={3}
 className="w-full rounded-md border border-slate-200 p-3 sm:text-sm text-xs focus:border-rose-500 focus:outline-none transition-all resize-none"
 placeholder="Lý do xuất kho, người nhận hàng, vị trí công trình..."
 />
 </div>
 </div>
 </div>
 </div>

 {/* Right Side: Material Summary & Visual Feedback */}
 <div className="space-y-4">
 <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
 <div className="p-4 space-y-4">
 <div className="flex h-48 w-full items-center justify-center overflow-hidden rounded-md bg-slate-50 border border-slate-100 text-rose-600">
 {item.thumbnail_url ? (
 <img src={item.thumbnail_url} alt={item.name} className="h-full w-full object-cover" />
 ) : (
 <Leaf className="h-16 w-16 opacity-10" />
 )}
 </div>
 
 <div className="space-y-1">
 <div className="text-rose-600 bg-rose-50 px-2 py-0.5 rounded-sm inline-block">
 {item.code}
 </div>
 <h3 className="text-slate-950 leading-tight">{item.name}</h3>
 <p className="text-xs text-slate-500">Đơn vị: {item.unit}</p>
 </div>

 <div className="space-y-3 pt-4 border-t border-slate-100">
 <div className="flex justify-between items-center sm:text-sm text-xs">
 <span className="text-slate-500">Tồn hiện tại:</span>
 <span className="text-slate-900 bg-slate-100 px-2 py-0.5 rounded-sm">{item.current_stock.toLocaleString()}</span>
 </div>
 <div className="flex justify-between items-center sm:text-sm text-xs">
 <span className="text-slate-500">Số lượng xuất:</span>
 <span className="text-rose-600">-{values.quantity || 0}</span>
 </div>
 <div className={cn(
"flex justify-between items-center rounded-md p-3 mt-4 transition-all",
 isOverStock ?"bg-rose-100 text-rose-600 border border-rose-200" :
 values.quantity > 0 ?"bg-rose-600 text-white -rose-600/20" :"bg-slate-50 text-slate-400"
 )}>
 <span className="text-xs tracking-wider opacity-80">Còn lại dự kiến</span>
 <span className="sm:text-sm text-xs">{remainingStock.toLocaleString()} {item.unit}</span>
 </div>
 </div>
 </div>
 </div>

 {isOverStock && (
 <div className="rounded-md border border-rose-200 bg-rose-50 p-4">
 <div className="flex gap-3 text-rose-800">
 <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5 animate-bounce" />
 <div className="space-y-1">
  <p className="sm:text-sm text-xs">Cảnh báo tồn kho</p>
  <p className="text-xs leading-relaxed opacity-80">Số lượng xuất vượt quá tồn kho khả dụng hiện tại. Vui lòng kiểm tra lại.</p>
 </div>
 </div>
 </div>
 )}

 <div className="rounded-md border border-slate-100 bg-slate-50/50 p-4">
 <div className="flex gap-3 text-slate-500">
 <Box className="h-5 w-5 shrink-0 mt-0.5" />
 <div className="space-y-1">
 <p className="sm:text-sm text-xs">Ghi chú kho</p>
 <p className="text-xs leading-relaxed opacity-80">Đảm bảo hàng hóa được đóng gói đúng quy cách trước khi đưa ra khỏi kho.</p>
 </div>
 </div>
 </div>
 </div>
 </div>

 {/* Premium Sticky Footer Actions */}
 <div className="sticky bottom-4 z-10 mt-4 flex items-center justify-end gap-3 rounded-md border border-slate-200 bg-white/80 backdrop-blur-md p-3">
 <button
 type="button"
 onClick={() => navigate('/inventory/items')}
 className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-6 sm:text-sm text-xs text-slate-700 hover:bg-slate-50 transition-all"
 >
 Hủy bỏ
 </button>
 <button
 type="button"
 onClick={() => setConfirmOpen(true)}
 disabled={isSaving || values.quantity <= 0 || isOverStock}
 className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-rose-600 px-10 sm:text-sm text-xs text-white -rose-600/20 hover:bg-rose-700 disabled:opacity-50 disabled:grayscale transition-all active:scale-95"
 >
 <Save className="h-4 w-4" />
 Xác nhận xuất kho
 </button>
 </div>

 <ConfirmDialog
 isOpen={confirmOpen}
 title="Xác nhận xuất kho"
 message={`Hệ thống sẽ thực hiện xuất ${values.quantity} ${item.unit} cho vật tư "${item.name}". Thao tác này không thể hoàn tác. Bạn có chắc chắn?`}
 confirmText="Hoàn tất xuất kho"
 isDestructive
 isLoading={isSaving}
 onConfirm={handleSave}
 onClose={() => setConfirmOpen(false)}
 />
 </div>
 );
}

export default InventoryStockOutPage;

